import type { Partner } from "api-types";

import type { Product } from "@dashboard/types/product";
import type { EntitySource } from "@dashboard/types/source";

const PARTNER_NAMES: Record<Partner, string> = {
  zonal: "Zonal",
  adaco: "Adaco",
  nory: "Nory",
  fourth: "Fourth",
  tevalis: "Tevalis",
  procurewizard: "Procure Wizard",
};

// Import history also groups MCP sources, whose partner is free text.
export const partnerDisplayName = (partner: Partner): string =>
  Object.hasOwn(PARTNER_NAMES, partner) ? PARTNER_NAMES[partner] : partner;

// MCP ingests stamp a free-text partner too, but only integrations re-sync
// and overwrite fields, so only they should lock them.
export const integrationPartnerNameOf = (
  source?: EntitySource | null
): string | null =>
  source && Object.hasOwn(PARTNER_NAMES, source.partner)
    ? PARTNER_NAMES[source.partner]
    : null;

// Keyed on partner and type, never on `source` alone: every other partner
// reports allergens rather than composition, so a blanket source check would
// lock the wrong fields for them.
export const isNoryBatch = (product?: Product | null): boolean =>
  product?.source?.partner === "nory" && product?.productType === "batch";

// Guards every write, not just the drawer: CSV import writes composition
// straight through. Ingredients may shrink but never grow, so existing drift
// can still be cleared.
export const guardNoryBatchWrite = (
  next: Product,
  stored: Product | undefined
): Product => {
  if (!stored || !isNoryBatch(stored)) return next;

  const storedIngredientIds = new Set(stored.ingredientIds);
  const onlyRemovesIngredients = next.ingredientIds.every((id) =>
    storedIngredientIds.has(id)
  );

  return {
    ...next,
    productIds: stored.productIds,
    ingredientIds: onlyRemovesIngredients
      ? next.ingredientIds
      : stored.ingredientIds,
  };
};
